import { useEffect, useRef, useState } from "react";

import { sameDay, startOfDay } from "../../lib/calendar";
import { TONE_CLASSES, afterState, meetingTitle, toneOf } from "../../lib/meetings-view";
import type { SalesMeeting } from "../../lib/sales-types";
import { cn } from "../ui/cn";
import { AfterIcon } from "./AfterIcon";
import { clock } from "./MeetingRow";

/**
 * The month view: six weeks, Monday first, as Google draws it.
 *
 * Three meetings a day and then "+N more", which opens the whole day in a
 * popover rather than growing the cell -- a month whose rows change height
 * depending on how busy they are is a month you cannot read across. The day
 * number is a link to that week, because a month is where you find a day and
 * a week is where you work it.
 *
 * Pressing empty space in a day books something on it, at nine, as a starting
 * point the form lets you change.
 */
const PER_DAY = 3;

export function MonthGrid({
  days,
  month,
  meetings,
  now,
  selectedId,
  onOpen,
  onPickWeek,
  onCreate,
}: {
  days: Date[];
  /** Any day in the month on show, to grey the days either side of it. */
  month: Date;
  meetings: SalesMeeting[];
  now: Date;
  selectedId: number | null;
  onOpen: (meeting: SalesMeeting, anchor: DOMRect) => void;
  onPickWeek: (day: Date) => void;
  onCreate: (start: Date, minutes: number) => void;
}) {
  /* The day whose "+N more" is open, and where its cell is on screen. Drawn
     fixed over the page rather than inside the cell, which clips. */
  const [more, setMore] = useState<{ day: Date; left: number; top: number } | null>(null);

  /*
   * Three a day at most -- but fewer when the cells are
   * short (a laptop with the meeting pane open, a phone), because a third
   * entry cut in half with "+N more" pushed out of sight is worse than two and
   * a visible "+2 more". Measured from the grid rather than guessed.
   */
  const grid = useRef<HTMLDivElement>(null);
  const [room, setRoom] = useState(200);
  const [chip, setChip] = useState(21);
  useEffect(() => {
    const element = grid.current;
    if (!element) return;
    const measure = (): void => {
      const wide = window.matchMedia("(min-width: 640px)").matches;
      /* A row, less the date at its head and the cell's padding. */
      setRoom(element.clientHeight / 6 - (wide ? 32 : 26));
      setChip(wide ? 21 : 17);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  /** How many of a day's meetings to draw, leaving a line for "+N more". */
  const visible = (count: number): number => {
    const all = Math.min(PER_DAY, Math.floor(room / chip));
    if (count <= all) return count;
    const MORE_LINE = 16;
    return Math.max(1, Math.min(PER_DAY, Math.floor((room - MORE_LINE) / chip)));
  };

  /* Five columns or seven: the weekend is only drawn when it has something
     on it (see `workingDays`), and the grid is always six weeks. */
  const columns = days.length / 6;

  const onDay = (day: Date) =>
    meetings.filter((meeting) => sameDay(new Date(meeting.startAt), day));

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        className="grid border-b border-line"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {days.slice(0, columns).map((day) => (
          <div
            key={day.getTime()}
            className="py-1.5 text-center text-[10px] font-medium uppercase tracking-wide text-faint"
          >
            {day.toLocaleDateString("en-GB", { weekday: "short" })}
          </div>
        ))}
      </div>

      <div
        ref={grid}
        className="grid min-h-0 flex-1 grid-rows-6"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {days.map((day) => {
          const today = sameDay(day, now);
          const inMonth = day.getMonth() === month.getMonth();
          const list = onDay(day);
          /* All of them when they fit; otherwise one fewer, to make room for
             the "+N more" that says how many are not shown. */
          const shownList = list.slice(0, visible(list.length));
          const hidden = list.length - shownList.length;

          return (
            <div
              key={day.getTime()}
              onClick={() => {
                const start = startOfDay(day);
                start.setHours(9);
                onCreate(start, 60);
              }}
              className={cn(
                "relative flex min-h-0 min-w-0 flex-col gap-px overflow-hidden border-b border-r border-line p-0.5 sm:p-1",
                !inMonth && "bg-surface-2/50",
              )}
            >
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onPickWeek(day);
                }}
                title="Open this week"
                className={cn(
                  "mx-auto flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] tabular-nums hover:bg-surface-2 sm:size-6 sm:text-xs",
                  today
                    ? "bg-brand-600 font-semibold text-white hover:bg-brand-700"
                    : inMonth
                      ? "text-content"
                      : "text-faint",
                )}
              >
                {day.getDate()}
              </button>

              {shownList.map((meeting) => (
                <MonthChip
                  key={meeting.id}
                  meeting={meeting}
                  now={now}
                  selected={selectedId === meeting.id}
                  onOpen={onOpen}
                />
              ))}

              {hidden > 0 ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    const cell = event.currentTarget.parentElement!.getBoundingClientRect();
                    setMore({
                      day,
                      /* Kept on screen at the right-hand edge and the bottom. */
                      left: Math.min(cell.left, window.innerWidth - 232),
                      top: Math.min(cell.top, window.innerHeight - 320),
                    });
                  }}
                  className="shrink-0 truncate rounded px-1 text-left text-[10px] font-medium leading-4 text-muted hover:bg-surface-2 sm:text-[11px]"
                >
                  +{hidden} more
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      {more ? (
        <DayPopover
          day={more.day}
          position={more}
          meetings={onDay(more.day)}
          now={now}
          onOpen={(meeting, anchor) => {
            setMore(null);
            onOpen(meeting, anchor);
          }}
          onClose={() => setMore(null)}
        />
      ) : null}
    </div>
  );
}

function MonthChip({
  meeting,
  now,
  selected,
  onOpen,
}: {
  meeting: SalesMeeting;
  now: Date;
  selected: boolean;
  onOpen: (meeting: SalesMeeting, anchor: DOMRect) => void;
}) {
  return (
    <button
      type="button"
      title={`${clock(meeting.startAt)} ${meetingTitle(meeting)}\n${meeting.crmCompanyName ?? meeting.companyName}`}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(meeting, event.currentTarget.getBoundingClientRect());
      }}
      className={cn(
        "flex w-full min-w-0 shrink-0 items-center gap-1 rounded border-l-2 px-1 text-left text-[10px] leading-4 sm:text-[11px] sm:leading-5",
        TONE_CLASSES[toneOf(meeting)],
        selected && "ring-2 ring-brand-500",
      )}
    >
      <span className="hidden shrink-0 tabular-nums opacity-80 sm:inline">
        {clock(meeting.startAt)}
      </span>
      <span className="min-w-0 flex-1 truncate font-medium">
        {meeting.subject?.trim() || meeting.companyName}
      </span>
      <AfterIcon state={afterState(meeting, now)} className="shrink-0" />
    </button>
  );
}

/** A whole day's meetings, over the cell, for the "+N more" press. */
function DayPopover({
  day,
  position,
  meetings,
  now,
  onOpen,
  onClose,
}: {
  day: Date;
  position: { left: number; top: number };
  meetings: SalesMeeting[];
  now: Date;
  onOpen: (meeting: SalesMeeting, anchor: DOMRect) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDown = (event: PointerEvent): void => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      onClick={(event) => event.stopPropagation()}
      style={{ left: Math.max(8, position.left), top: Math.max(8, position.top) }}
      className="fixed z-50 max-h-80 w-56 space-y-px overflow-y-auto rounded-lg border border-line bg-surface p-2 shadow-xl"
    >
      <p className="mb-1 text-center text-xs font-medium text-content">
        {day.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
      </p>
      {meetings.map((meeting) => (
        <MonthChip key={meeting.id} meeting={meeting} now={now} selected={false} onOpen={onOpen} />
      ))}
    </div>
  );
}
